import { Alert, Badge, Breadcrumb, Button, Col, Dropdown, Modal, OverlayTrigger, Pagination, Row, Spinner, Table, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import LoggedInLayout from "../../layouts/loggedin-layout";
import { useAuditLogsQuery } from "../../new/auditlogs.query";
import styles from "../../styles/AuditLogs.module.css";

const pageSizeOptions = [10, 25, 50, 100];
const eventTypeFilters = [
    "PHI_READ",
    "PHI_CREATE",
    "PHI_UPDATE",
    "PHI_DELETE",
    "SEARCH",
    "FILE",
    "REPORT",
    "ADMINISTRATION",
    "AUTHENTICATION",
    "AUTHORIZATION",
    "SECURITY",
    "EXPORT"
];

const formatDateTime = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "--";
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    return value;
};

const formatJson = (value) => {
    if (!value) {
        return "--";
    }

    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
};

const outcomeVariant = (outcome) => {
    switch ((outcome ?? "").toLowerCase()) {
        case "success":
            return "success";
        case "failed":
        case "failure":
            return "danger";
        default:
            return "secondary";
    }
};

const AuditLogs = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [activeEventType, setActiveEventType] = useState("all");
    const [selectedLog, setSelectedLog] = useState(null);
    const queryParams = useMemo(() => ({
        page,
        pageSize,
        eventType: activeEventType === "all" ? null : activeEventType
    }), [activeEventType, page, pageSize]);
    const auditLogsQuery = useAuditLogsQuery(queryParams);
    const data = auditLogsQuery.data;
    const items = data?.items ?? [];
    const totalCount = data?.totalCount ?? 0;
    const currentPage = data?.page ?? page;
    const currentPageSize = data?.pageSize ?? pageSize;
    const totalPages = Math.max(1, Math.ceil(totalCount / currentPageSize));
    const startItem = totalCount === 0 ? 0 : ((currentPage - 1) * currentPageSize) + 1;
    const endItem = Math.min(currentPage * currentPageSize, totalCount);

    useEffect(() => {
        document.title = "MFP - Audit Logs";
    }, []);

    useEffect(() => {
        if (!auditLogsQuery.isFetching && totalCount > 0 && currentPage > totalPages) {
            setPage(totalPages);
        }
    }, [auditLogsQuery.isFetching, currentPage, totalCount, totalPages]);

    const handlePageSizeChange = (nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
    };

    const handleEventTypeChange = (eventType) => {
        setActiveEventType(eventType);
        setPage(1);
    };

    return (
        <LoggedInLayout>
            <div style={{padding: "25px"}}>
                <div className={styles.auditHeader}>
                    <div className={styles.auditTitleGroup}>
                        <h3>Audit Logs</h3>
                        <Breadcrumb>
                            <Breadcrumb.Item className="primarybreadcrumb">Admin</Breadcrumb.Item>
                            <Breadcrumb.Item className="secondarybreadcrumb" active>Audit Logs</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                    <OverlayTrigger placement="bottom" overlay={<Tooltip>Refresh logs</Tooltip>}>
                        <Button
                            type="button"
                            variant="primary"
                            className={styles.detailsButton}
                            onClick={() => auditLogsQuery.refetch()}
                            disabled={auditLogsQuery.isFetching}
                            aria-label="Refresh logs"
                        >
                            {auditLogsQuery.isFetching ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <FontAwesomeIcon icon={faRotateRight} />
                            )}
                        </Button>
                    </OverlayTrigger>
                </div>

                <div className="seperator mb-3"></div>

                <div className={styles.filterBar} aria-label="Audit event type filters">
                    <button
                        type="button"
                        className={`${styles.filterPill} ${activeEventType === "all" ? styles.filterPillActive : ""}`}
                        onClick={() => handleEventTypeChange("all")}
                        disabled={auditLogsQuery.isFetching && activeEventType !== "all"}
                    >
                        All
                    </button>
                    {eventTypeFilters.map((eventType) => (
                        <button
                            type="button"
                            key={eventType}
                            className={`${styles.filterPill} ${activeEventType === eventType ? styles.filterPillActive : ""}`}
                            onClick={() => handleEventTypeChange(eventType)}
                            disabled={auditLogsQuery.isFetching && activeEventType !== eventType}
                        >
                            {eventType}
                        </button>
                    ))}
                </div>

                <div className={styles.toolbar}>
                    <span className={styles.pagingSummary}>
                        {startItem} - {endItem} of {totalCount}
                    </span>
                    <div className={styles.pageSizeControls}>
                        <span className={styles.pagingSummary}>Rows</span>
                        <Dropdown>
                            <Dropdown.Toggle className="dropdowncustom" id="audit-page-size" disabled={auditLogsQuery.isFetching}>
                                {currentPageSize}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {pageSizeOptions.map((option) => (
                                    <Dropdown.Item
                                        as="button"
                                        key={option}
                                        type="button"
                                        onClick={() => handlePageSizeChange(option)}
                                    >
                                        {option}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>

                {auditLogsQuery.isError && (
                    <Alert variant="danger">
                        Audit logs could not be loaded.
                    </Alert>
                )}

                {!auditLogsQuery.isError && auditLogsQuery.isLoading && (
                    <div className="d-flex align-items-center justify-content-center py-5">
                        <Spinner animation="border" className="me-2" />
                        <span>Loading</span>
                    </div>
                )}

                {!auditLogsQuery.isError && !auditLogsQuery.isLoading && items.length === 0 && (
                    <div className={styles.emptyState}>
                        No audit logs found.
                    </div>
                )}

                {!auditLogsQuery.isError && items.length > 0 && (
                    <>
                        <div className={styles.tableWrap}>
                            <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Occurred</th>
                                        <th>Actor</th>
                                        <th>Event</th>
                                        <th>Action</th>
                                        <th>Outcome</th>
                                        <th>Resource</th>
                                        <th>Application</th>
                                        <th>Rows</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.id}>
                                            <td className={styles.compactCell}>{formatDateTime(item.occurredUtc)}</td>
                                            <td>{formatValue(item.actorUsername)}</td>
                                            <td>{formatValue(item.eventType)}</td>
                                            <td>{formatValue(item.action)}</td>
                                            <td>
                                                <Badge bg={outcomeVariant(item.outcome)}>
                                                    {formatValue(item.outcome)}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div>{formatValue(item.resourceType)}</div>
                                                <small className={styles.mutedCell}>{formatValue(item.resourceId)}</small>
                                            </td>
                                            <td>{formatValue(item.applicationId)}</td>
                                            <td>{formatValue(item.rowsReturned ?? item.rowsAffected)}</td>
                                            <td className="text-center">
                                                <OverlayTrigger placement="left" overlay={<Tooltip>View details</Tooltip>}>
                                                    <Button
                                                        type="button"
                                                        variant="primary"
                                                        className={styles.detailsButton}
                                                        onClick={() => setSelectedLog(item)}
                                                        aria-label="View audit log details"
                                                    >
                                                        <FontAwesomeIcon icon={faCircleInfo} />
                                                    </Button>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        <Pagination className="justify-content-end mt-3">
                            <Pagination.Prev
                                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                                disabled={currentPage === 1 || auditLogsQuery.isFetching}
                            />
                            <Pagination.Item active>{currentPage}</Pagination.Item>
                            <Pagination.Next
                                onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                                disabled={currentPage >= totalPages || auditLogsQuery.isFetching}
                            />
                        </Pagination>
                    </>
                )}
            </div>
            <AuditLogDetailsModal log={selectedLog} onHide={() => setSelectedLog(null)} />
        </LoggedInLayout>
    );
};

const AuditLogDetailsModal = ({ log, onHide }) => {
    if (!log) {
        return null;
    }

    return (
        <Modal show={Boolean(log)} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Audit Log Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <div className={styles.detailsGrid}>
                            <DetailItem label="Occurred" value={formatDateTime(log.occurredUtc)} />
                            <DetailItem label="Actor" value={log.actorUsername} />
                            <DetailItem label="Actor User ID" value={log.actorUserId} />
                            <DetailItem label="Outcome" value={log.outcome} />
                            <DetailItem label="Event Type" value={log.eventType} />
                            <DetailItem label="Action" value={log.action} />
                            <DetailItem label="Failure Reason" value={log.failureReason} />
                            <DetailItem label="Purpose Of Use" value={log.purposeOfUse} />
                            <DetailItem label="PHI Involved" value={log.phiInvolved} />
                            <DetailItem label="Resource Type" value={log.resourceType} />
                            <DetailItem label="Resource ID" value={log.resourceId} />
                            <DetailItem label="Application ID" value={log.applicationId} />
                            <DetailItem label="Rows Returned" value={log.rowsReturned} />
                            <DetailItem label="Rows Affected" value={log.rowsAffected} />
                            <DetailItem label="Duration" value={log.durationMs === null || log.durationMs === undefined ? null : `${log.durationMs} ms`} />
                            <DetailItem label="HTTP Method" value={log.httpMethod} />
                            <DetailItem label="Endpoint" value={log.endpoint} />
                            <DetailItem label="IP Address" value={log.ipAddress} />
                            <DetailItem label="Correlation ID" value={log.correlationId} />
                            <DetailItem label="Request ID" value={log.requestId} />
                            <DetailItem label="Session Hash" value={log.sessionIdHash} mono />
                            <DetailItem label="Medicaid Blind Index" value={log.medicaidIdBlindIndex} mono />
                            <DetailItem label="Event Hash" value={log.eventHash} mono />
                            <DetailItem label="Previous Event Hash" value={log.previousEventHash} mono />
                            <DetailItem label="Hash Version" value={log.hashVersion} />
                            <DetailItem label="User Agent" value={log.userAgent} />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <h6 className="fw-bold">Fields Touched</h6>
                        <pre className={styles.jsonBlock}>{formatJson(log.fieldsTouchedJson)}</pre>
                    </Col>
                    <Col xs={12} md={6}>
                        <h6 className="fw-bold">Metadata</h6>
                        <pre className={styles.jsonBlock}>{formatJson(log.metadataJson)}</pre>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const DetailItem = ({ label, value, mono = false }) => (
    <div className={styles.detailItem}>
        <span className={styles.detailLabel}>{label}</span>
        <span className={`${styles.detailValue} ${mono ? styles.hashText : ""}`}>
            {formatValue(value)}
        </span>
    </div>
);

export default AuditLogs;
